import React, { useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import Barcode from 'react-barcode';
import html2canvas from 'html2canvas';

const PUBLIC_URL = 'https://lotsense.thewholesalecars.com';

// Get end use indicator: B for byrider/dealer_direct, W for wholesale
const getEndUseIndicator = (endUse) => {
  if (!endUse) return null;
  const lower = endUse.toLowerCase();
  if (lower === 'byrider' || lower === 'dealer_direct') return 'B';
  if (lower === 'wholesale') return 'W';
  return null;
};

// QR Code Label Component - supports different sizes for different printers
const VehicleQRCode = ({ vehicle, onClose }) => {
  const printRef = useRef();
  const [size, setSize] = useState('keytag');

  if (!vehicle) return null;

  const qrUrl = `${PUBLIC_URL}/car/${vehicle.stockNumber}`;
  const ymm = `${vehicle.year} ${vehicle.make} ${vehicle.model}`;
  const endUseIndicator = getEndUseIndicator(vehicle.endUse || vehicle.end_use);

  // Save key tag as image for NIIMBOT printer
  const handleSaveAsImage = async () => {
    const element = printRef.current?.querySelector('.label');
    if (!element) return;
    
    try {
      const canvas = await html2canvas(element, {
        scale: 3, // Higher resolution for print quality
        backgroundColor: '#ffffff',
        useCORS: true,
      });
      
      // Create download link
      const link = document.createElement('a');
      link.download = `keytag-${vehicle.stockNumber}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      console.error('Failed to save image:', err);
      alert('Failed to save image. Try the print option instead.');
    }
  };

  // Print windshield label normally
  const handlePrint = () => {
    const printContent = printRef.current;
    const printWindow = window.open('', '_blank');
    
    printWindow.document.write(`
      <html>
        <head>
          <title>Print Label - ${vehicle.stockNumber}</title>
          <style>
            @page { margin: 0; }
            body { 
              margin: 0; 
              padding: 0;
              font-family: Arial, sans-serif;
            }
            .label {
              display: flex;
              align-items: center;
              justify-content: center;
              page-break-after: always;
            }
            .keytag {
              width: 2in;
              height: 1.2in;
              padding: 0.1in;
            }
            .windshield {
              width: 4in;
              height: 6in;
              padding: 0.25in;
              flex-direction: column;
            }
            .qr-container {
              text-align: center;
            }
            .info {
              font-size: 8pt;
              line-height: 1.2;
            }
            .windshield .info {
              font-size: 14pt;
              margin-top: 0.25in;
            }
            .stock {
              font-weight: bold;
              font-size: 10pt;
            }
            .windshield .stock {
              font-size: 18pt;
            }
            .ymm {
              font-weight: bold;
            }
            .vin {
              font-family: monospace;
              font-size: 7pt;
            }
            .windshield .vin {
              font-size: 10pt;
            }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-lg w-full" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-neutral-200 flex justify-between items-center">
          <h2 className="font-semibold text-lg">Print QR Label</h2>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600 text-2xl">&times;</button>
        </div>

        <div className="p-6">
          {/* Size selector */}
          <div className="flex gap-2 mb-6">
            <button
              onClick={() => setSize('keytag')}
              className={`flex-1 py-2 px-4 rounded-lg border-2 ${size === 'keytag' ? 'border-blue-500 bg-blue-50' : 'border-neutral-200'}`}
            >
              <div className="font-medium">Key Tag</div>
              <div className="text-xs text-neutral-500">2" x 1.2" (NIIMBOT)</div>
            </button>
            <button
              onClick={() => setSize('windshield')}
              className={`flex-1 py-2 px-4 rounded-lg border-2 ${size === 'windshield' ? 'border-blue-500 bg-blue-50' : 'border-neutral-200'}`}
            >
              <div className="font-medium">Windshield</div>
              <div className="text-xs text-neutral-500">4" x 6" Thermal</div>
            </button>
          </div>

          {/* Preview */}
          <div className="border border-neutral-200 rounded-lg p-4 bg-neutral-50 mb-6">
            <div className="text-xs text-neutral-500 mb-2 text-center">Preview</div>
            
            <div ref={printRef}>
              {size === 'keytag' ? (
                /* Key Tag Label - 2" x 1.2" */
                <div className="label keytag bg-white border border-neutral-300 mx-auto" style={{ width: '2in', height: '1.2in', display: 'flex', alignItems: 'center', padding: '0.1in', position: 'relative' }}>
                  <div className="qr-container" style={{ marginRight: '0.1in' }}>
                    <QRCodeSVG value={qrUrl} size={60} level="M" />
                  </div>
                  <div className="info" style={{ fontSize: '8pt', lineHeight: 1.2 }}>
                    <div className="stock" style={{ fontWeight: 'bold', fontSize: '10pt' }}>#{vehicle.stockNumber}</div>
                    <div className="ymm" style={{ fontWeight: 'bold' }}>{ymm}</div>
                    <div>{vehicle.color || ''}{endUseIndicator && <span> | EU={endUseIndicator}</span>}</div>
                    <div className="vin" style={{ fontFamily: 'monospace', fontSize: '6pt' }}>{vehicle.vin?.slice(-8)}</div>
                    <div style={{ fontSize: '6pt' }}>{vehicle.mileage ? `${Number(vehicle.mileage).toLocaleString()} mi` : ''}</div>
                  </div>
                  <div style={{ position: 'absolute', bottom: '2px', right: '4px', fontSize: '6pt', color: '#555', fontWeight: '500' }}>LotSense DMS</div>
                </div>
              ) : (
                /* Windshield Label - 4" x 6" */
                <div className="label windshield bg-white border border-neutral-300 mx-auto" style={{ width: '4in', height: '6in', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '0.3in', position: 'relative' }}>
                  <div className="qr-container">
                    <QRCodeSVG value={qrUrl} size={200} level="M" />
                  </div>
                  <div className="info" style={{ marginTop: '0.3in', textAlign: 'center', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-around' }}>
                    <div className="stock" style={{ fontWeight: 'bold', fontSize: '32pt' }}>#{vehicle.stockNumber}</div>
                    <div className="ymm" style={{ fontWeight: 'bold', fontSize: '22pt' }}>{ymm}</div>
                    <div style={{ fontSize: '18pt' }}>{vehicle.color || ''}{endUseIndicator && <span> | EU={endUseIndicator}</span>}</div>
                    <div style={{ fontSize: '20pt', fontWeight: '500' }}>{vehicle.mileage ? `${Number(vehicle.mileage).toLocaleString()} miles` : ''}</div>
                    <div>
                      <div className="vin" style={{ fontFamily: 'monospace', fontSize: '14pt', marginBottom: '0.1in' }}>VIN: {vehicle.vin}</div>
                      {vehicle.vin && (
                        <Barcode value={vehicle.vin} width={1.5} height={40} fontSize={0} margin={0} />
                      )}
                    </div>
                  </div>
                  <div style={{ paddingTop: '0.15in', fontSize: '11pt', color: '#666' }}>
                    Scan QR for details
                  </div>
                  <div style={{ position: 'absolute', bottom: '6px', right: '8px', fontSize: '8pt', color: '#555', fontWeight: '500' }}>LotSense DMS</div>
                </div>
              )}
            </div>
          </div>

          {/* Action button - Save as image for keytag, Print for windshield */}
          {size === 'keytag' ? (
            <button
              onClick={handleSaveAsImage}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-3 rounded-lg transition"
            >
              Save as Image (for NIIMBOT)
            </button>
          ) : (
            <button
              onClick={handlePrint}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 rounded-lg transition"
            >
              Print Label
            </button>
          )}

          <p className="text-xs text-neutral-400 text-center mt-3">
            QR links to: {qrUrl}
          </p>
        </div>
      </div>
    </div>
  );
};

// Standalone QR button to add to vehicle cards/details
export const QRButton = ({ vehicle, className = '' }) => {
  const [showModal, setShowModal] = React.useState(false);

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className={`text-neutral-500 hover:text-blue-600 ${className}`}
        title="Print QR Label"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h2M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
        </svg>
      </button>
      {showModal && <VehicleQRCode vehicle={vehicle} onClose={() => setShowModal(false)} />}
    </>
  );
};

// QR Code Popup - Quick inline display for scanning (not printing)
// Dismissable via click outside, X button, or Escape key
export const QRCodePopup = ({ vehicle, className = '' }) => {
  const [showPopup, setShowPopup] = React.useState(false);
  const popupRef = React.useRef(null);

  // Handle Escape key to close popup
  React.useEffect(() => {
    if (!showPopup) return;
    
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setShowPopup(false);
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showPopup]);

  if (!vehicle) return null;

  const qrUrl = `${PUBLIC_URL}/car/${vehicle.stockNumber}`;
  const ymm = `${vehicle.year} ${vehicle.make} ${vehicle.model}`;

  return (
    <div className="relative inline-block">
      <button
        onClick={(e) => { e.stopPropagation(); setShowPopup(!showPopup); }}
        className={`text-neutral-500 hover:text-blue-600 transition-colors ${className}`}
        title="Show QR Code"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h2M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
        </svg>
      </button>
      
      {showPopup && (
        <>
          {/* Backdrop - click to close */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setShowPopup(false)}
          />
          
          {/* Popup */}
          <div 
            ref={popupRef}
            className="absolute z-50 bg-white rounded-lg shadow-xl border border-neutral-200 p-4 mt-2 -ml-20"
            style={{ minWidth: '200px' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button 
              onClick={() => setShowPopup(false)}
              className="absolute top-2 right-2 text-neutral-400 hover:text-neutral-600 text-lg leading-none"
            >
              &times;
            </button>
            
            {/* QR Code */}
            <div className="flex flex-col items-center">
              <QRCodeSVG value={qrUrl} size={150} level="M" />
              <div className="mt-3 text-center">
                <div className="font-bold text-sm">#{vehicle.stockNumber}</div>
                <div className="text-xs text-neutral-600">{ymm}</div>
                <div className="text-xs text-neutral-400 mt-1">Scan to view details</div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default VehicleQRCode;
